import { FilterOperator, FilterValueType } from "../../constants/FilterConstants.js";
import { templateParseToElement, templateParseToElementMultipleIds } from "../../logic/templateParse.js";
import { ColumnDataFilterSubset } from "../../types/ListManagerTypes.js";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { FilterBaseComponent } from "./filter-base-component.js";
import { FilterInputRangeComponent } from "./filter-input-range-component.js";
import { FilterOperatorsComponent } from "./filter-operators-component.js";

export class FilterInputComponent extends BaseWebComponent {
    static webComponentName: string = "filter-input";
    protected _templateId: string = "filter-input-template";
    private _componentData: Partial<Record<string, any>> = {};
    private _colData!: ColumnDataFilterSubset;
    private _value: any;
    private inputId: string;
    constructor() {
        super();
        this.inputId = this.createRandomId();
    }
    protected get filterValueType(): FilterValueType {
        return this.colData?.filterValueType || 'string';
    }
    get colData(): ColumnDataFilterSubset {
        return this._colData;
    }
    get value(): any {
        if (this.isNullRelatedOperator()) {
            return undefined; // Return undefined for null-related operators
        }
        return this._value;
    }
    set colData(cdata: ColumnDataFilterSubset) {
        this._colData = cdata;
        this._componentData = {
            filterValueType: this.filterValueType,
        };
        this.render();
    }
    get defaultFilterValue(): any {
        let val: any = this.colData.defaultFilterValue || null;
        if (this.filterValueType === 'boolean') {
            val = val ? true : false; // Ensure boolean type
        }
        return val;
    }
    private set value(val: any) {
        this._value = val;
        this.dispatch('change-value-input', { value: this._value });
        console.log(`FilterInputComponent: value set to ${val}`);
        // if (this._value != undefined) {}
    }
    protected _operator!: FilterOperator;

    get operator(): FilterOperator {
        return this._operator;
    }

    set operator(value: FilterOperator) {
        const changed = this._operator !== value;
        this._operator = value;
        this.evalNullRelatedCondition();
        if (changed) {
            this.render();
        }
    }
    protected onConnected(): void {
        const parentElement = this.closest('[role="filter-element"]');
        if (parentElement) {
            (parentElement as FilterBaseComponent).addEventListener('operator-change', (e: Event) => {
                this.operator = (e as CustomEvent).detail.value;
            });
        }
    }
    private isNullRelatedOperator(): boolean {
        return this.operator && (this.operator === 'IS NULL' || this.operator === 'IS NOT NULL');
    }
    private isRangeOperator(): boolean {
        return this.operator && (this.operator === 'BETWEEN');
    }
    private evalNullRelatedCondition(): void {
        if (this.isNullRelatedOperator()) {
            this.classList.add('d-none');
        }
        else {
            this.classList.remove('d-none');
        }
    }
    private setupEventListeners(): void {
        const inputSource = this.querySelector(`[data-input-source]`)
        if (inputSource) {
            if (inputSource instanceof HTMLInputElement) {
                inputSource.addEventListener('input', (e: Event) => {
                    const inputValue = (e.target as HTMLInputElement).value;
                    const inputType = (e.target as HTMLInputElement).type;
                    if (inputType === 'checkbox') {
                        this.handleTriStateCheckbox(e.target as HTMLInputElement);
                    } else {
                        this.value = inputValue;
                    }
                });
            }
            else if (inputSource instanceof FilterInputRangeComponent) {
                inputSource.addEventListener('change-input', (e: Event) => {
                    const rangeValue = (e.target as FilterInputRangeComponent).value;
                    if (rangeValue) {
                        this.value = rangeValue;
                    }
                });
            }
        }
    }
    /**
     * Handle tri-state checkbox for boolean filters
     * States: indeterminate (no filter) → true → false → indeterminate
     * Uses data attribute to track actual state since DOM properties can be unreliable
     */
    private handleTriStateCheckbox(checkbox: HTMLInputElement): void {
        const label = checkbox.parentElement?.querySelector('.checkbox-state-label') as HTMLSpanElement;

        // Use data attribute to track current state more reliably
        const currentState = checkbox.dataset.triState || 'indeterminate';
        // Prevent default checkbox behavior
        checkbox.checked = false;

        switch (currentState) {
            case 'indeterminate':
                // indeterminate → true
                checkbox.indeterminate = false;
                checkbox.checked = true;
                checkbox.dataset.triState = 'true';
                this.value = true;
                if (label) label.textContent = 'Sì';
                break;

            case 'true':
                // true → false
                checkbox.indeterminate = false;
                checkbox.checked = false;
                checkbox.dataset.triState = 'false';
                this.value = false;
                if (label) label.textContent = 'No';
                break;

            case 'false':
            default:
                // Reset to indeterminate if unknown state
                checkbox.indeterminate = true;
                checkbox.checked = false;
                checkbox.dataset.triState = 'indeterminate';
                this.value = undefined; // No filter applied
                if (label) label.textContent = 'Tutti';
        }
    }

    render(): void {
        if (!this._colData) return; //impedisce il rendering se colData non è definito
        this.innerHTML = "" // Placeholder for actual rendering logic
        const filterType = this.colData?.filterType || 'simple';
        const filterValueType = this.filterValueType;
        const defaultFilterValue = this.defaultFilterValue;
        templateParseToElementMultipleIds([
            `filter-input-${filterType}-${filterValueType}-template`, // es. filter-input-simple-string-template
            `filter-input-${filterValueType}-template`, // es. filter-input-string-template
            this.templateId // es. filter-input-template
        ], this, {
            defaultFilterValue,
            filterValueType,
            colData: this.colData,
            inputId: this.inputId,
            isNullRelatedOperator: this.isNullRelatedOperator(),
            isRangeOperator: this.isRangeOperator(),
        });
        this.setupEventListeners();
        this.initializeTriStateCheckbox();
    }

    /**
     * Initialize tri-state checkbox to indeterminate state
     */
    private initializeTriStateCheckbox(): void {
        const checkbox = this.querySelector('[data-indeterminate="true"]') as HTMLInputElement;
        if (checkbox) {
            checkbox.indeterminate = true;
            checkbox.checked = false;
            checkbox.dataset.triState = 'indeterminate';
            this.value = undefined; // No filter applied initially
            const label = checkbox.parentElement?.querySelector('.checkbox-state-label') as HTMLSpanElement;
            if (label) label.textContent = 'Tutti';
        }
    }
}